import { type MetaFunction, useLoaderData } from 'react-router'
import { formatPhone } from '#app/utils/help-contact.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pageMeta } from '#app/utils/page-meta.ts'

export const meta: MetaFunction = (args) =>
	pageMeta(args, {
		title: 'Sals- og avbestillingsvilkår',
		description:
			'Vilkåra for kjøp og avbestilling av billettar til Huskonsertfestivalen på Vorrå.',
	})

const ARRANGOR =
	'Vorrå Huskonsert Festival (org.nr 937 219 113), Hasselvegen 6, 4265 Håvik'

const ANSVAR =
	'Arrangør og vert svarer ikkje for tap eller skade på person eller eigedom som ikkje skuldast grov aktløyse frå arrangør eller vert.'

const SECTIONS = [
	{
		title: 'Generelt',
		items: [
			'Vorrå Huskonsertfestival blir arrangert 27.–29. november 2026 på Vorrå-feltet på Karmøy.',
			`Arrangør: ${ARRANGOR}`,
			'Konsertane blir gjennomførte i private heimar og naust. Talet på plassar per konsert er avgrensa.',
		],
	},
	{
		title: 'Prisar',
		items: [
			'Kvar konsert har eigne billettar. Prisen for kvar billettype står på konsertsida og i kassa før du betaler. Alle prisar er i norske kroner.',
			'Prisane er etter evne: vel sjølv mellom Generøs, Ordinær og Lavpris, den som passar for deg.',
			'Barn (12 år og under) går gratis, men treng òg ein reservert plass.',
		],
	},
	{
		title: 'Bestilling og betaling',
		items: [
			'Billettar blir bestilte på huskonsertfestivalen.no med ein innlogga brukar. Billettane du vel, held av plassen din med ein gong.',
			'I kassa vel du om du vil betale no med Vipps, eller betale i døra med Vipps eller kontant før konserten startar.',
			'Betaler du med Vipps, blir beløpet trekt når du godkjenner betalinga i Vipps-appen. Då er billettane betalte.',
			'Nettsida lagrar ingen kort- eller kontoopplysningar. Betalinga blir handtert av Vipps.',
		],
	},
	{
		title: 'Avbestilling og refusjon',
		items: [
			'Ein billett du ikkje har betalt enno, kan du fjerne sjølv i kassa. Då blir plassen ledig for andre.',
			'Vil du avbestille ein betalt billett, gi beskjed til arrangøren på telefon eller e-post (sjå Kontakt).',
			'Alle avbestillingar blir refunderte i sin heilskap, same når avbestillinga skjer.',
			'Betalingar med Vipps blir refunderte til den same Vipps-kontoen. Det kan ta nokre dagar før pengane er tilbake.',
			'Du kan òg overlate plassen din til ein annan. Gi beskjed til arrangøren om kven som tek over.',
			'Dersom ein konsert blir avlyst (til dømes på grunn av sjukdom eller force majeure), blir alle billettmidlar refunderte i sin heilskap.',
		],
	},
	{
		title: 'Angrerett',
		items: [
			'Billettar til arrangement på ein bestemt dato har ikkje angrerett (angrerettlova § 22 bokstav m). Du kan likevel avbestille og få pengane tilbake, sjå Avbestilling og refusjon.',
		],
	},
	{
		title: 'Klage',
		items: [
			'Er noko gale med billetten, betalinga eller arrangementet, ta kontakt med arrangøren så snart som mogleg på telefon eller e-post (sjå Kontakt).',
			'Blir vi ikkje einige, kan du klage til Forbrukarrådet (forbrukerradet.no).',
		],
	},
	{
		title: 'Levering og billettering',
		items: [
			'Billettane ligg på brukaren din på huskonsertfestivalen.no, med ein QR-kode som blir vist ved innsjekk. QR-koden kan visast på mobil eller skrivast ut. Ingen fysisk billett blir sendt.',
		],
	},
	{
		title: 'Deltaking og ansvar',
		items: [
			'Du deltek for eiga rekning og risiko. Konsertane blir haldne i private heimar og naust, og du følgjer instruksjonane frå huseigaren om parkering og tilgang.',
			ANSVAR,
			'Deltakarar som oppfører seg upassande eller er rusa, kan visast bort utan refusjon.',
		],
	},
]

const FOOTER_NOTE =
	'Vilkåra gjeld alt sal av billettar til Vorrå Huskonsertfestival og blir avgjorde etter norsk lov. Endringar blir varsla på huskonsertfestivalen.no.'

/** The organiser's contact, from the current festival. */
export async function loader() {
	const festival = await prisma.festival.findFirst({
		where: { isCurrent: true },
		select: { contactName: true, contactPhone: true, contactEmail: true },
	})
	return {
		contact: {
			name: festival?.contactName ?? null,
			phone: festival?.contactPhone ?? null,
			email: festival?.contactEmail ?? null,
		},
	}
}

export default function TermsRoute() {
	const { contact } = useLoaderData<typeof loader>()
	return (
		<main className="font-poppins grid p-4 pb-16">
			<div className="mx-auto my-8 w-full max-w-4xl">
				<h1 className="mb-4 text-3xl font-bold">
					Sals- og avbestillingsvilkår
				</h1>
				{SECTIONS.map((section) => (
					<section key={section.title} className="mb-8">
						<h2 className="mb-2 text-xl font-semibold">{section.title}</h2>
						<ul className="list-disc pl-5">
							{section.items.map((item, i) => (
								<li key={i} className="mb-2">
									{item}
								</li>
							))}
						</ul>
					</section>
				))}
				<section className="mb-8">
					<h2 className="mb-2 text-xl font-semibold">Kontakt</h2>
					<ul className="list-disc pl-5">
						<li className="mb-2">{ARRANGOR}</li>
						{contact.name ? <li className="mb-2">{contact.name}</li> : null}
						{contact.phone ? (
							<li className="mb-2">
								Telefon:{' '}
								<a href={`tel:${contact.phone.replace(/\s/g, '')}`}>
									{formatPhone(contact.phone)}
								</a>
							</li>
						) : null}
						{contact.email ? (
							<li className="mb-2">
								E-post: <a href={`mailto:${contact.email}`}>{contact.email}</a>
							</li>
						) : null}
					</ul>
				</section>
				<p className="text-sm text-base-content/70">{FOOTER_NOTE}</p>
			</div>
		</main>
	)
}
